import { createUri, HpQueryValueMap, voidFunction } from '@avantia/client-and-server-utilities';
import { HpQuestionSet } from '@avantia/questionset-model';
import { registerConfigurationAction } from '../config/registerClientConfig';
import { HpMetaData } from '../tools/clientAndServerUtilities';
import { setAnswersIdCookie, setProductIdCookie } from '../tools/cookieUtilities';
import { setExperimentsInQuery } from '../tools/utilities';
import { HpDispatchFunction } from '../types';
import { fetchResolver, fetchResolverBasic } from './fetchResolver';
import { HpGetQuestionSetActionProps, HpQuestionSetPayload } from './getQuestionSetActionTypes';

let websiteEndpoint: string;
registerConfigurationAction((config) => {
  ({ websiteEndpoint } = config);
});

export function getQuestionSetAction(
  props: HpGetQuestionSetActionProps
): (dispatch: HpDispatchFunction<HpQuestionSetPayload>) => Promise<void> {
  const { keepAnswers } = props;
  return (dispatch: HpDispatchFunction<HpQuestionSetPayload>): Promise<void> => {
    const prm = getQuestionSet(props);
    prm.catch(voidFunction);
    return fetchResolver<never, HpQuestionSet>({
      loadingPageType: keepAnswers ? 'overlay' : undefined,
      options: prm.then((questionSet) => {
        const { answersId, productId } = questionSet;
        if (answersId) {
          setAnswersIdCookie(answersId);
          setProductIdCookie(productId);
        }

        return questionSet;
      }),
      dispatch: dispatch as any,
      successAction: (questionSet) => {
        dispatch({ type: 'QUESTIONSET_RETRIEVED', payload: { questionSet, keepAnswers: keepAnswers === true } });
      }
    });
  };
}

export function getQuestionSet({
  productId,
  questionSetId,
  metadata,
  queryParams,
  setCustomAnswers
}: HpGetQuestionSetActionProps): Promise<HpQuestionSet> {
  if (!productId) {
    return Promise.reject('The "productId" must contain a value.');
  }

  queryParams = createQueryParamsIfNotProvided(metadata, queryParams);
  setExperimentsInQuery(queryParams);

  const url = createUri({
    uri: questionSetId
      ? `${websiteEndpoint}/api/questionset/by/id/${encodeURIComponent(questionSetId)}`
      : `${websiteEndpoint}/api/questionset/by/productid/${encodeURIComponent(productId)}`,
    query: queryParams
  });

  const promise = fetchResolverBasic<never, HpQuestionSet>({
    url,
    method: 'GET',
    description: `Get question set ID ${productId}`
  });

  promise.catch(voidFunction);

  return promise.then((questionSet) => {
    if (setCustomAnswers) {
      const { answers, productId, questions } = questionSet;
      questionSet.answers = setCustomAnswers({
        answers,
        productId,
        questions,
        setAnswersId: (answersId) => {
          questionSet.answersId = answersId;
        }
      });
    }

    return questionSet;
  });
}

export function createQueryParamsIfNotProvided(
  metadata: HpMetaData | undefined,
  queryParams: HpQueryValueMap | undefined
): HpQueryValueMap {
  if (!queryParams) {
    queryParams = {};
    const { agentId, source } = metadata || {};
    if (agentId) {
      queryParams.agentId = agentId;
    }

    if (source) {
      queryParams.source = source;
    }
  }

  return queryParams;
}
